import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@mkcore/ui';
import { EmailCode, PortalCard, startEntra } from '@/features/portal/login';

type Info =
  | { status: 'invalid' }
  | { status: 'expired'; organisationName: string }
  | { status: 'valid'; organisationName: string; firstName: string; loginMethod: 'entra' | 'email_code'; email: string | null };

/** The invitation link of a contact (CO-0662, ADR-0028): the first login binds the account to the contact. */
export const Route = createFileRoute('/portal/invite/$token')({
  component: Invitation,
});

function Invitation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = Route.useParams();
  const info = useQuery({
    queryKey: ['portal', 'invitation', token],
    queryFn: async () => (await fetch(`/api/v1/portal/invitations/${encodeURIComponent(token)}`)).json() as Promise<Info>,
  });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const entra = async () => {
    setBusy(true);
    const res = await fetch(`/api/v1/portal/invitations/${encodeURIComponent(token)}/accept`, { method: 'POST', credentials: 'same-origin' });
    if (!res.ok || !(await startEntra('/portal'))) {
      setBusy(false);
      setFailed(true);
    }
  };
  const i = info.data;
  if (!i)
    return (
      <PortalCard title={t('portal.invite.title')}>
        <Spinner label={t('common.loading')} />
      </PortalCard>
    );
  if (i.status !== 'valid')
    return (
      <PortalCard title={t('portal.invite.title')}>
        <p role="alert" className="text-sm">
          {i.status === 'expired' ? t('portal.invite.expired', { organisation: i.organisationName }) : t('portal.invite.invalid')}
        </p>
      </PortalCard>
    );
  return (
    <PortalCard title={t('portal.invite.welcome', { name: i.firstName })} subtitle={t('portal.invite.subtitle', { organisation: i.organisationName })}>
      {failed && (
        <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger">
          {t('portal.login.failed')}
        </p>
      )}
      {i.loginMethod === 'entra' ? (
        <>
          <p className="text-sm text-fg-2">{t('portal.invite.entraHint')}</p>
          <Button size="lg" loading={busy} onClick={() => void entra()}>
            {t('portal.login.microsoft')}
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm text-fg-2">{t('portal.invite.codeHint')}</p>
          <EmailCode email={i.email ?? undefined} onSignedIn={() => navigate({ to: '/portal' })} />
        </>
      )}
    </PortalCard>
  );
}
